/**
 * How tall the account tabs module is. On the V1 card every tab is capped at 720px; the V2 module is capped at
 * `OTHER_TAB_MAX_PX` (1070px). Either way content scrolls inside. The Hierarchy tab autofits its canvas between a minimum and a maximum that leaves room for
 * the tab strip and header inside the 720px module; the table view lives in the same capped box and
 * scrolls within it. Shared by the V1 card (`AccountDrillDown`) and the V2 tabs module (`AccountLens`).
 */
export const HIERARCHY_CANVAS_MIN_PX = 520;
/** The canvas / table / sidebar box: the 720px module minus the tab strip, header row and padding. */
export const HIERARCHY_CANVAS_MAX_PX = 580;
/** The V2 tabs module cap (was 720; raised by 350px so reps can drag it taller). V1 card keeps its own 720px class. */
export const OTHER_TAB_MAX_PX = 1070;
/** Breathing room around the laid-out tree inside the canvas. */
const CANVAS_PAD_PX = 48;

/** Canvas height for a laid-out tree of `layoutHeight` px: the tree plus padding, within [minimum, maximum]. */
export function canvasHeightPx(layoutHeight: number): number {
  return Math.min(HIERARCHY_CANVAS_MAX_PX, Math.max(HIERARCHY_CANVAS_MIN_PX, Math.round(layoutHeight + CANVAS_PAD_PX)));
}

/** V1 right card: Hierarchy autofits (592px minimum) and every tab is capped at 720px; content scrolls inside. */
export function v1RightCardClass(hierarchy: boolean): string {
  const base = 'bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-lg flex flex-col';
  return hierarchy
    ? `${base} min-h-[592px] max-h-[720px] overflow-y-auto`
    : `${base} h-[592px] max-h-[720px]`;
}

/**
 * V2 tabs panel: autofit tabs (Hierarchy and, from U4, Product Usage) get `{ minHeight: 600, maxHeight: 1070 }`;
 * others get a rep-resizable `height`. Every tab is capped at `OTHER_TAB_MAX_PX`.
 */
export function v2TabsPanelStyle(autofit: boolean, tabsHeight: number): { minHeight: number; maxHeight: number } | { height: number; maxHeight: number } {
  return autofit
    ? { minHeight: 600, maxHeight: OTHER_TAB_MAX_PX }
    : { height: tabsHeight, maxHeight: OTHER_TAB_MAX_PX };
}
